import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Train, Shuffle, Clock, ArrowRight,
    ChevronRight, AlertCircle, Info
} from 'lucide-react';
import type { ConnectingTrainResult } from '../api/stationsSchedules';
import './ConnectingTrainDetailsPage.css';

const ConnectingTrainDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const connectingTrain = location.state?.connectingTrain as ConnectingTrainResult | undefined;

    if (!connectingTrain) {
        return (
            <div className="ctd-error-screen" role="alert">
                <AlertCircle size={40} />
                <h2>Journey Not Found</h2>
                <p>This connecting journey configuration is no longer accessible or active.</p>
                <button onClick={() => navigate(-1)} className="ctd-back-btn">
                    Go Back
                </button>
            </div>
        );
    }

    const { leg_1, leg_2, transfer, total } = connectingTrain;

    const handleLegClick = (trainNumber: string) => {
        navigate(`/trains/${trainNumber}`);
    };

    return (
        <main className="ctd-page">
            <div className="ctd-container">

                {/* Top Navigation Navigation Bar */}
                <header className="ctd-topbar">
                    <button className="ctd-back" onClick={() => navigate(-1)} aria-label="Go back to search results">
                        <ArrowLeft size={18} />
                        <span>Back to Results</span>
                    </button>
                    <span className="ctd-topbar-label">Multi-Leg Configuration</span>
                </header>

                {/* Hero Summary Segment (Premium Ticket Stub Style) */}
                <section className="ctd-hero">
                    <div className="ctd-hero-rail"></div>
                    <div className="ctd-hero-body">
                        <div className="ctd-hero-route">
                            <div className="ctd-hero-station">
                                <span className="ctd-hero-time">{leg_1.from.departure}</span>
                                <span className="ctd-hero-name">{leg_1.from.station_name}</span>
                            </div>
                            <div className="ctd-hero-arrow">
                                <div className="ctd-hero-line" />
                                <div className="ctd-hero-icon-wrap">
                                    <Shuffle size={18} />
                                </div>
                                <div className="ctd-hero-line" />
                            </div>
                            <div className="ctd-hero-station ctd-hero-station--right">
                                <span className="ctd-hero-time">{leg_2.to.arrival}</span>
                                <span className="ctd-hero-name">{leg_2.to.station_name}</span>
                            </div>
                        </div>

                        <div className="ctd-perf"></div>

                        <div className="ctd-hero-meta">
                            <div className="ctd-meta-pill">
                                <span className="ctd-meta-pill-label">Total Duration</span>
                                <span className="ctd-meta-pill-val">{total.formatted}</span>
                            </div>
                            <div className="ctd-meta-sep"></div>
                            <div className="ctd-meta-pill">
                                <span className="ctd-meta-pill-label">Transfers</span>
                                <span className="ctd-meta-pill-val ctd-transfer-val">1 Layover</span>
                            </div>
                            <div className="ctd-meta-sep"></div>
                            <div className="ctd-meta-pill">
                                <span className="ctd-meta-pill-label">Total Scale</span>
                                <span className="ctd-meta-pill-val">2 Trains</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Journey Grid Split Panels Layout */}
                <div className="ctd-layout-grid">

                    {/* Left Column Panel: Interactive Chronological Timeline */}
                    <section className="ctd-main-panel">
                        <h2 className="ctd-section-title">Journey Operational Timeline</h2>
                        <div className="ctd-visual-timeline">

                            {/* Origin Entry Node */}
                            <div className="ctd-vt-row">
                                <div className="ctd-vt-spine">
                                    <div className="ctd-vt-dot ctd-vt-dot--start" />
                                    <div className="ctd-vt-line" />
                                </div>
                                <div className="ctd-vt-info">
                                    <span className="ctd-vt-time">{leg_1.from.departure}</span>
                                    <span className="ctd-vt-station">{leg_1.from.station_name}</span>
                                    <span className="ctd-vt-label">Origin Departure</span>
                                </div>
                            </div>

                            {/* Operational Transit Track 1 */}
                            <div className="ctd-vt-row ctd-vt-row--transit">
                                <div className="ctd-vt-spine">
                                    <div className="ctd-vt-line ctd-vt-line--transit" />
                                </div>
                                <div className="ctd-vt-info">
                                    <div className="ctd-vt-train-badge ctd-badge-violet">
                                        <Train size={12} />
                                        <span>{leg_1.train_name} (#{leg_1.train_number})</span>
                                    </div>
                                </div>
                            </div>

                            {/* Intermediate Transfer Intersecting Node */}
                            <div className="ctd-vt-row">
                                <div className="ctd-vt-spine">
                                    <div className="ctd-vt-line" />
                                    <div className="ctd-vt-dot ctd-vt-dot--transfer">
                                        <Shuffle size={11} />
                                    </div>
                                    <div className="ctd-vt-line" />
                                </div>
                                <div className="ctd-vt-info">
                                    <span className="ctd-vt-time">{leg_1.to.arrival}</span>
                                    <span className="ctd-vt-station">{leg_1.to.station_name}</span>
                                    <div className="ctd-transfer-chip">
                                        <Clock size={11} />
                                        <span>{transfer.formatted} Connection Window</span>
                                    </div>
                                </div>
                            </div>

                            {/* Operational Transit Track 2 */}
                            <div className="ctd-vt-row ctd-vt-row--transit">
                                <div className="ctd-vt-spine">
                                    <div className="ctd-vt-line ctd-vt-line--transit" />
                                </div>
                                <div className="ctd-vt-info">
                                    <div className="ctd-vt-train-badge ctd-badge-indigo">
                                        <Train size={12} />
                                        <span>{leg_2.train_name} (#{leg_2.train_number})</span>
                                    </div>
                                </div>
                            </div>

                            {/* Terminal Target Drop Node */}
                            <div className="ctd-vt-row">
                                <div className="ctd-vt-spine">
                                    <div className="ctd-vt-dot ctd-vt-dot--end" />
                                </div>
                                <div className="ctd-vt-info ctd-vt-info--final">
                                    <span className="ctd-vt-time">{leg_2.to.arrival}</span>
                                    <span className="ctd-vt-station">{leg_2.to.station_name}</span>
                                    <span className="ctd-vt-label">Terminal Destination Arrival</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Right Column Panel: Dynamic Interactive Telemetry Cards */}
                    <div className="ctd-sidebar-panel">
                        <h2 className="ctd-section-title">Train Assignment Particulars</h2>

                        {/* Leg 1 Interactive Tracking Object Card */}
                        <article
                            className="ctd-leg-card"
                            onClick={() => handleLegClick(leg_1.train_number)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleLegClick(leg_1.train_number)}
                            aria-label={`View schedule parameters for ${leg_1.train_name}`}
                        >
                            <div className="ctd-leg-rail ctd-leg-rail-1"></div>
                            <div className="ctd-leg-body">
                                <div className="ctd-leg-header">
                                    <div className="ctd-leg-meta-block">
                                        <span className="ctd-leg-badge ctd-badge-violet">Leg 1</span>
                                        <h3 className="ctd-leg-train-name">{leg_1.train_name}</h3>
                                        <span className="ctd-leg-train-num">#{leg_1.train_number}</span>
                                    </div>
                                    <div className="ctd-leg-duration">
                                        <Clock size={12} />
                                        <span>{Math.floor(leg_1.duration_mins / 60)}h {leg_1.duration_mins % 60}m</span>
                                    </div>
                                </div>

                                <div className="ctd-leg-route">
                                    <div className="ctd-leg-stop">
                                        <span className="ctd-leg-time">{leg_1.from.departure}</span>
                                        <span className="ctd-leg-stn">{leg_1.from.station_name}</span>
                                    </div>
                                    <div className="ctd-leg-arrow">
                                        <div className="ctd-leg-line" />
                                        <ArrowRight size={14} />
                                    </div>
                                    <div className="ctd-leg-stop ctd-leg-stop--right">
                                        <span className="ctd-leg-time">{leg_1.to.arrival}</span>
                                        <span className="ctd-leg-stn">{leg_1.to.station_name}</span>
                                    </div>
                                </div>

                                <div className="ctd-leg-footer">
                                    <span>Inspect Fleet Timetable</span>
                                    <ChevronRight size={14} />
                                </div>
                            </div>
                        </article>

                        {/* Middle Connection Banner Interface */}
                        <div className="ctd-transfer-banner">
                            <div className="ctd-transfer-icon-wrap">
                                <Shuffle size={16} />
                            </div>
                            <div className="ctd-transfer-info">
                                <span className="ctd-transfer-title">Transit Point: {leg_1.to.station_name}</span>
                                <span className="ctd-transfer-time">
                                    <Clock size={12} /> <strong>{transfer.formatted}</strong> platform layover duration
                                </span>
                            </div>
                            <div className="ctd-transfer-warning">
                                <Info size={13} />
                                <span>Buffer Guard Active</span>
                            </div>
                        </div>

                        {/* Leg 2 Interactive Tracking Object Card */}
                        <article
                            className="ctd-leg-card"
                            onClick={() => handleLegClick(leg_2.train_number)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleLegClick(leg_2.train_number)}
                            aria-label={`View schedule parameters for ${leg_2.train_name}`}
                        >
                            <div className="ctd-leg-rail ctd-leg-rail-2"></div>
                            <div className="ctd-leg-body">
                                <div className="ctd-leg-header">
                                    <div className="ctd-leg-meta-block">
                                        <span className="ctd-leg-badge ctd-badge-indigo">Leg 2</span>
                                        <h3 className="ctd-leg-train-name">{leg_2.train_name}</h3>
                                        <span className="ctd-leg-train-num">#{leg_2.train_number}</span>
                                    </div>
                                    <div className="ctd-leg-duration">
                                        <Clock size={12} />
                                        <span>{Math.floor(leg_2.duration_mins / 60)}h {leg_2.duration_mins % 60}m</span>
                                    </div>
                                </div>

                                <div className="ctd-leg-route">
                                    <div className="ctd-leg-stop">
                                        <span className="ctd-leg-time">{leg_2.from.departure}</span>
                                        <span className="ctd-leg-stn">{leg_2.from.station_name}</span>
                                    </div>
                                    <div className="ctd-leg-arrow">
                                        <div className="ctd-leg-line" />
                                        <ArrowRight size={14} />
                                    </div>
                                    <div className="ctd-leg-stop ctd-leg-stop--right">
                                        <span className="ctd-leg-time">{leg_2.to.arrival}</span>
                                        <span className="ctd-leg-stn">{leg_2.to.station_name}</span>
                                    </div>
                                </div>

                                <div className="ctd-leg-footer">
                                    <span>Inspect Fleet Timetable</span>
                                    <ChevronRight size={14} />
                                </div>
                            </div>
                        </article>

                        {/* Structured Manifest Accounting Footer Summary Matrix */}
                        <div className="ctd-summary">
                            <div className="ctd-summary-row">
                                <span>Initial Departure</span>
                                <strong>{leg_1.from.departure} · {leg_1.from.station_name}</strong>
                            </div>
                            <div className="ctd-summary-row">
                                <span>Terminal Drop</span>
                                <strong>{leg_2.to.arrival} · {leg_2.to.station_name}</strong>
                            </div>
                            <div className="ctd-summary-row">
                                <span>Layover Intermission</span>
                                <strong>{transfer.formatted}</strong>
                            </div>
                            <div className="ctd-summary-row ctd-summary-row--total">
                                <span>Consolidated Duration</span>
                                <strong>{total.formatted}</strong>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </main>
    );
};

export default ConnectingTrainDetailsPage;